import { ENV } from '@common/app/env';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { isAppleIapAvailable } from '@/common/lib/apple-iap';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { type RootState } from '@/redux/store';

const baseQuery = fetchBaseQuery({
  baseUrl: ENV.API_BACKEND_URL,
  prepareHeaders: (headers, { getState }) => {
    const initData = Telegram?.WebApp?.initData as string | undefined;
    const params = new URLSearchParams(initData);
    const startParam = params.get('start_param');
    const urlParams = new URLSearchParams(window.location.search);
    const isPlaydeck = urlParams.get('playdeck') === 'true';
    const token = (getState() as RootState).user.token;

    if (token) {
      headers.set('X-SESSION', token);
    }

    if (startParam && startParam.length === 64 && !isPlaydeck) {
      headers.set('HASH', startParam);
    }

    return headers;
  },
  credentials: 'include',
}) as BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

const mutex = new Mutex();

// iOS app: the purchase UI still says "XTR" (Stars), but the App Store is the
// only way to pay there — the server answers with a StoreKit invoice instead.
const toAppleCurrency = (args: string | FetchArgs): string | FetchArgs => {
  if (
    typeof args === 'string' ||
    !isAppleIapAvailable() ||
    !args.body ||
    typeof args.body !== 'object' ||
    !/^xtr$/i.test(String((args.body as { currency?: unknown }).currency))
  ) {
    return args;
  }

  return { ...args, body: { ...args.body, currency: 'apple' } };
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (rawArgs, api, extraOptions) => {
  const args = toAppleCurrency(rawArgs);
  const state = api.getState() as RootState;

  if (state.user.maintenanceEndTime) {
    return {
      error: { status: 502, data: { wakeUpAt: state.user.maintenanceEndTime } },
    };
  }

  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error?.status === 503) {
    const errorData = result.error.data as { wakeUpAt?: string } | undefined;

    if (errorData?.wakeUpAt) {
      api.dispatch({
        type: 'authUser/setMaintenanceMode',
        payload: errorData.wakeUpAt,
      });
    }

    return result;
  }

  if (result.error && result.error?.status === 401) {
    const initDataIsExist = !!Telegram.WebApp.initData;

    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        if (initDataIsExist) {
          const refreshResult = await baseQuery(
            {
              url: '/auth/telegram/login',
              method: 'POST',
              headers: { 'X-TG-INIT': Telegram?.WebApp?.initData ?? '' },
            },
            api,
            extraOptions,
          );

          if (refreshResult.data) {
            // retry the initial query
            result = await baseQuery(args, api, extraOptions);
          } else {
            // re-auth failed — make it terminal so queries stop retrying
            // login on every poll/refetch (otherwise infinite login storm)
            showToast('The session has ended. Please log in again', undefined, {
              toastId: 'session-ended',
            });
            localStorage.removeItem('auth_token');
            api.dispatch({
              payload: undefined,
              type: 'authUser/loggedOut',
            });
          }
        } else {
          showToast('The session has ended. Please log in again', undefined, {
            toastId: 'session-ended',
          });
          localStorage.removeItem('auth_token');
          api.dispatch({
            payload: undefined,
            type: 'authUser/loggedOut',
          });
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BOOST', 'QUARRY', 'FACTORY', 'WORKSHOP', 'UserDecorations'],
  endpoints: () => ({}),
});

export { api };
